import { useEffect, useRef, type ReactNode } from "react";

import { Button } from "./ui";

/** Modal dialog built on the native <dialog> element (focus trap + Escape handled by the browser). */
export function Dialog({
  open,
  title,
  children,
  onClose,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /** Large dialog (file viewer, email preview). */
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    } else if (!open && el.open) {
      if (typeof el.close === "function") el.close();
      else el.removeAttribute("open");
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="dialog-title"
      onClose={onClose}
      onCancel={onClose}
      className={`m-auto rounded-lg border border-line p-0 shadow-xl backdrop:bg-black/40 ${
        wide ? "w-[min(96vw,64rem)]" : "w-[min(92vw,32rem)]"
      }`}
    >
      {open && (
        <div className="p-6">
          <h2 id="dialog-title" className="text-lg font-bold">
            {title}
          </h2>
          <div className="mt-3 text-ink">{children}</div>
          <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div>
        </div>
      )}
    </dialog>
  );
}

/** Confirmation dialog for destructive or important actions. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Go back
          </Button>
          <Button variant={danger ? "danger" : "primary"} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}
